import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';

import { CreateEntityCommandService } from '../create-entity-command.service';
import { CreateEntityReferenceDataService } from '../create-entity-reference-data.service';
import { CreateEntityWizardStore } from '../create-entity-wizard.store';
import { COUNTRY_STATES, sortCountriesByPriority } from '../shared/country-states.constant';
import { FACILITY_TYPES } from './entity-form.contants';
import { FACILITY_FORM_SCHEMA } from './facility-form.schema-validators';
import { DEFAULT_FACILITY_FORM_VALUE, FACILITY_FORM_KIND } from './facility-form.types';


/**
 * Facility Details + Address Details form, extracted from
 * `CreateEntityElementComponent` so each entity tab owns its own form.
 *
 * See `OrganizationFormComponent` for why this doesn't need its own
 * ShadowDom encapsulation or duplicated CSS.
 *
 * Countries are fetched from the real Jemma API (shared codeset with the
 * Organization tab, via `JemmaOrganizationApiService.getCountries()`), gated
 * the same way `OrganizationFormComponent` gates its reference-data fetch.
 * Facility Type stays a static/local-only field — the real
 * facility-creation endpoint has no facility-type concept.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'wcg-facility-form',
    imports: [FormField, MatFormField, MatSelect, MatOption],
    templateUrl: './facility-form.component.html'
})
export class FacilityFormComponent {
    private readonly _commands = inject(CreateEntityCommandService);
    private readonly _wizard = inject(CreateEntityWizardStore);
    private readonly _referenceData = inject(CreateEntityReferenceDataService);

    /** Whether this panel should be visually hidden (the "inactive" tabs stay in the DOM so their data is never lost). */
    readonly hidden = input(false);

    readonly facilityTypes = FACILITY_TYPES;

    readonly $countries = computed(() => sortCountriesByPriority(this._referenceData.$countries()));
    readonly $referenceDataError = this._referenceData.$countriesError;

    /** Typed Signal Form, mirroring the Organization tab and the dashboard's `CreateOrganizationDialogComponent` pattern. */
    readonly $model = signal({ ...DEFAULT_FACILITY_FORM_VALUE });
    readonly $form = form(this.$model, FACILITY_FORM_SCHEMA);

    /**
     * State/province options for the currently selected country, mirroring the Organization tab
     * and the dashboard's `wcg-address` component: only United States and Canada have a curated
     * list, so every other country falls back to a free-text state/province input (see template).
     */
    readonly $states = computed(() => COUNTRY_STATES.get(this.$form.country().value()) ?? []);

    constructor() {
        effect(() => {
            const _country = this.$form.country().value();
            untracked(() => {
                /*
                 * @NOTE:- Mirrors the dashboard's `AddressComponent._stateManagementEffect` (and the
                 * Organization tab's identical effect): the previously-entered state/province may not
                 * exist for the new country's dropdown, so reset it. The `touched()` guard skips this
                 * on initial mount.
                 * */
                if (_country && this.$form.country().touched()) {
                    this.$form.state().reset('');
                }
            });
        });

        /** "Save" was pressed while this tab was active — see `CreateEntityWizardStore` for why every `*FormComponent` reacts to its own tab's command instead of the parent holding a `viewChild` reference. */
        effect(() => {
            const _request = this._wizard.submitRequest();
            if (_request?.tab !== FACILITY_FORM_KIND) {
                return;
            }

            untracked(() => this._trySubmit());
        });

        /** "+ Add Another" was pressed after this tab's entity was just created. */
        effect(() => {
            const _request = this._wizard.resetRequest();
            if (_request?.tab !== FACILITY_FORM_KIND) {
                return;
            }

            untracked(() => this.reset());
        });
    }

    /** Triggered by the native `ngSubmit` when the user presses Enter inside the form; the "Save" button instead goes through `CreateEntityWizardStore.requestSubmit()`. */
    onSubmit(): void {
        this._trySubmit();
    }

    /** Resets the form back to its blank initial state (used by the "+ Add Another" flow). */
    reset(): void {
        this.$form().reset({ ...DEFAULT_FACILITY_FORM_VALUE });
    }

    private _trySubmit(): void {
        if (this._wizard.submitting()) {
            return;
        }

        if (this.$form().invalid()) {
            this.$form().markAsTouched();
            return;
        }

        this._commands.createFacility(this.$form().value());
    }
}
